import { CommonModule } from '@angular/common';
import { Component, Input, TemplateRef } from '@angular/core';

export type SpreadsheetGridColumnKind = 'id' | 'text' | 'number' | 'date' | 'status';

export interface SpreadsheetGridColumnDefinition {
  key: string;
  headerPath: string[];
  width: string;
  band?: string;
  kind?: SpreadsheetGridColumnKind;
  inputMode?: 'text' | 'numeric' | 'decimal';
  placeholder?: string;
}

export interface SpreadsheetGridEdgeColumnDefinition {
  key: string;
  headerPath: string[];
  width: string;
  band?: string;
}

export interface SpreadsheetGridRow {
  id: number | string;
  cells: string[];
}

export interface SpreadsheetGridFooterRow {
  id?: number | string;
  leftLabel?: string;
  rightLabel?: string;
  cells: Array<string | number | null | undefined>;
}

export interface SpreadsheetGridCellContext {
  row: SpreadsheetGridRow;
  rowIndex: number;
  column: SpreadsheetGridColumnDefinition;
  columnIndex: number;
  value: string;
}

export interface SpreadsheetGridEdgeCellContext {
  edgeColumn: SpreadsheetGridEdgeColumnDefinition;
  row?: SpreadsheetGridRow;
  rowIndex?: number;
  footerRow?: SpreadsheetGridFooterRow;
}

export interface SpreadsheetGridFooterCellContext {
  footerRow: SpreadsheetGridFooterRow;
  column: SpreadsheetGridColumnDefinition;
  columnIndex: number;
  value: string | number | null | undefined;
}

type SpreadsheetGridLeafColumn =
  | {
      type: 'left-edge' | 'right-edge';
      key: string;
      headerPath: string[];
      width: string;
      band: string;
      edgeColumn: SpreadsheetGridEdgeColumnDefinition;
      normalizedHeaderPath: Array<string | null>;
    }
  | {
      type: 'data';
      key: string;
      headerPath: string[];
      width: string;
      band: string;
      kind: SpreadsheetGridColumnKind;
      column: SpreadsheetGridColumnDefinition;
      columnIndex: number;
      normalizedHeaderPath: Array<string | null>;
    };

type SpreadsheetGridHeaderCellRole = 'group' | 'leaf' | 'stacked';

type SpreadsheetGridHeaderCell = {
  id: string;
  label: string;
  rowStart: number;
  rowSpan: number;
  columnStart: number;
  columnSpan: number;
  band: string;
  role: SpreadsheetGridHeaderCellRole;
};

@Component({
  selector: 'app-spreadsheet-grid',
  standalone: true,
  imports: [CommonModule],
  template: require('./spreadsheet-grid.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./spreadsheet-grid.component.scss').default],
})
export class SpreadsheetGridComponent {
  @Input() columns: SpreadsheetGridColumnDefinition[] = [];
  @Input() rows: SpreadsheetGridRow[] = [];
  @Input() leftEdgeColumn: SpreadsheetGridEdgeColumnDefinition | null = null;
  @Input() rightEdgeColumn: SpreadsheetGridEdgeColumnDefinition | null = null;
  @Input() footerRows: SpreadsheetGridFooterRow[] = [];
  @Input() cellTemplate: TemplateRef<SpreadsheetGridCellContext> | null = null;
  @Input() leftEdgeCellTemplate: TemplateRef<SpreadsheetGridEdgeCellContext> | null = null;
  @Input() rightEdgeCellTemplate: TemplateRef<SpreadsheetGridEdgeCellContext> | null = null;
  @Input() footerCellTemplate: TemplateRef<SpreadsheetGridFooterCellContext> | null = null;
  @Input() footerLeftEdgeTemplate: TemplateRef<SpreadsheetGridEdgeCellContext> | null = null;
  @Input() footerRightEdgeTemplate: TemplateRef<SpreadsheetGridEdgeCellContext> | null = null;

  get headerDepth(): number {
    return Math.max(1, ...this.leafColumns.map((leaf) => leaf.headerPath.length));
  }

  get gridTemplateColumns(): string {
    return this.leafColumns.map((leaf) => `minmax(0, ${leaf.width})`).join(' ');
  }

  get headerCells(): SpreadsheetGridHeaderCell[] {
    const leafColumns = this.leafColumns;
    const depth = this.headerDepth;
    const cells: SpreadsheetGridHeaderCell[] = [];

    for (let level = 0; level < depth; level += 1) {
      let columnIndex = 0;

      while (columnIndex < leafColumns.length) {
        const leaf = leafColumns[columnIndex];
        const label = leaf.normalizedHeaderPath[level];
        if (!label) {
          columnIndex += 1;
          continue;
        }

        const prefixKey = leaf.normalizedHeaderPath.slice(0, level + 1).join('\u0001');
        let columnSpan = 1;

        while (columnIndex + columnSpan < leafColumns.length) {
          const nextLeaf = leafColumns[columnIndex + columnSpan];
          const nextLabel = nextLeaf.normalizedHeaderPath[level];
          const nextPrefixKey = nextLeaf.normalizedHeaderPath.slice(0, level + 1).join('\u0001');
          if (nextLabel !== label || nextPrefixKey !== prefixKey) {
            break;
          }
          columnSpan += 1;
        }

        const rowSpan = leaf.normalizedHeaderPath.slice(level + 1).every((part) => !part) ? depth - level : 1;
        const role: SpreadsheetGridHeaderCellRole =
          rowSpan > 1 ? 'stacked' : level === depth - 1 ? 'leaf' : 'group';

        cells.push({
          id: `${prefixKey}:${columnIndex}`,
          label,
          rowStart: level + 1,
          rowSpan,
          columnStart: columnIndex + 1,
          columnSpan,
          band: leaf.band,
          role,
        });

        columnIndex += columnSpan;
      }
    }

    return cells;
  }

  get leafColumns(): SpreadsheetGridLeafColumn[] {
    const depth = Math.max(
      1,
      this.leftEdgeColumn?.headerPath.length || 0,
      this.rightEdgeColumn?.headerPath.length || 0,
      ...this.columns.map((column) => column.headerPath.length)
    );

    const leaves: SpreadsheetGridLeafColumn[] = [];

    if (this.leftEdgeColumn) {
      leaves.push({
        type: 'left-edge',
        key: this.leftEdgeColumn.key,
        headerPath: this.leftEdgeColumn.headerPath,
        width: this.leftEdgeColumn.width,
        band: this.leftEdgeColumn.band || 'edge',
        edgeColumn: this.leftEdgeColumn,
        normalizedHeaderPath: this.normalizeHeaderPath(this.leftEdgeColumn.headerPath, depth),
      });
    }

    this.columns.forEach((column, columnIndex) => {
      leaves.push({
        type: 'data',
        key: column.key,
        headerPath: column.headerPath,
        width: column.width,
        band: column.band || 'default',
        kind: column.kind || 'text',
        column,
        columnIndex,
        normalizedHeaderPath: this.normalizeHeaderPath(column.headerPath, depth),
      });
    });

    if (this.rightEdgeColumn) {
      leaves.push({
        type: 'right-edge',
        key: this.rightEdgeColumn.key,
        headerPath: this.rightEdgeColumn.headerPath,
        width: this.rightEdgeColumn.width,
        band: this.rightEdgeColumn.band || 'edge',
        edgeColumn: this.rightEdgeColumn,
        normalizedHeaderPath: this.normalizeHeaderPath(this.rightEdgeColumn.headerPath, depth),
      });
    }

    return leaves;
  }

  trackByRowId(_index: number, row: SpreadsheetGridRow): number | string {
    return row.id;
  }

  trackByLeafKey(_index: number, leaf: SpreadsheetGridLeafColumn): string {
    return leaf.key;
  }

  trackByHeaderCellId(_index: number, cell: SpreadsheetGridHeaderCell): string {
    return cell.id;
  }

  trackByFooterRowId(index: number, row: SpreadsheetGridFooterRow): string | number {
    return row.id ?? index;
  }

  headerCellClassNames(cell: SpreadsheetGridHeaderCell): string[] {
    return [
      'spreadsheet-grid-header-cell',
      `spreadsheet-grid-header-${cell.role}`,
      `spreadsheet-grid-band-${cell.band}`,
    ];
  }

  leafClassNames(leaf: SpreadsheetGridLeafColumn): string[] {
    const classes = ['spreadsheet-grid-cell', `spreadsheet-grid-band-${leaf.band}`];
    if (leaf.type === 'data') {
      classes.push('spreadsheet-grid-data-cell', `spreadsheet-grid-kind-${leaf.kind}`);
    } else {
      classes.push('spreadsheet-grid-edge-cell', `spreadsheet-grid-edge-${leaf.type}`);
    }
    return classes;
  }

  footerLeafClassNames(leaf: SpreadsheetGridLeafColumn): string[] {
    const classes = ['spreadsheet-grid-footer-cell', `spreadsheet-grid-band-${leaf.band}`];
    if (leaf.type === 'data') {
      classes.push(`spreadsheet-grid-kind-${leaf.kind}`);
    } else {
      classes.push('spreadsheet-grid-footer-edge-cell');
    }
    return classes;
  }

  leftEdgeContext(row: SpreadsheetGridRow, rowIndex: number): SpreadsheetGridEdgeCellContext | null {
    if (!this.leftEdgeColumn) {
      return null;
    }
    return {
      edgeColumn: this.leftEdgeColumn,
      row,
      rowIndex,
    };
  }

  rightEdgeContext(row: SpreadsheetGridRow, rowIndex: number): SpreadsheetGridEdgeCellContext | null {
    if (!this.rightEdgeColumn) {
      return null;
    }
    return {
      edgeColumn: this.rightEdgeColumn,
      row,
      rowIndex,
    };
  }

  footerLeftEdgeContext(footerRow: SpreadsheetGridFooterRow): SpreadsheetGridEdgeCellContext | null {
    if (!this.leftEdgeColumn) {
      return null;
    }
    return {
      edgeColumn: this.leftEdgeColumn,
      footerRow,
    };
  }

  footerRightEdgeContext(footerRow: SpreadsheetGridFooterRow): SpreadsheetGridEdgeCellContext | null {
    if (!this.rightEdgeColumn) {
      return null;
    }
    return {
      edgeColumn: this.rightEdgeColumn,
      footerRow,
    };
  }

  cellContext(
    row: SpreadsheetGridRow,
    rowIndex: number,
    leaf: Extract<SpreadsheetGridLeafColumn, { type: 'data' }>
  ): SpreadsheetGridCellContext {
    return {
      row,
      rowIndex,
      column: leaf.column,
      columnIndex: leaf.columnIndex,
      value: row.cells[leaf.columnIndex],
    };
  }

  footerCellContext(
    footerRow: SpreadsheetGridFooterRow,
    leaf: Extract<SpreadsheetGridLeafColumn, { type: 'data' }>
  ): SpreadsheetGridFooterCellContext {
    return {
      footerRow,
      column: leaf.column,
      columnIndex: leaf.columnIndex,
      value: footerRow.cells[leaf.columnIndex],
    };
  }

  private normalizeHeaderPath(headerPath: string[], depth: number): Array<string | null> {
    return [...headerPath, ...Array.from({ length: Math.max(0, depth - headerPath.length) }, () => null)];
  }
}
